import { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';

import { cn } from '../../../shared/lib/cn';
import { rangeTime } from '../../../shared/lib/format';
import { ErrorState, LoadingState } from '../../../shared/ui/states';
import { StatusBars, type IBucketRange, type TBand } from '../../../shared/ui/status-bars';
import {
  RUN_CHART_SERIES,
  RUN_GROUPS,
  runChart,
  runChartBuckets,
  runKeys,
  runQuery,
  useRunChartLive,
  type IRunFilters,
} from '../../../entities/run';

// Also what the window moves by: the axis shifts by whole bars, never by a sliver.
const CHART_BUCKETS = 48;

const DOT: Record<TBand, string> = {
  success: 'bg-success',
  failure: 'bg-danger',
  suppressed: 'bg-suppressed',
  motion: 'bg-motion',
};

const tickOf = (minutes: number) =>
  Math.min(Math.max((minutes * 60_000) / CHART_BUCKETS, 15_000), 300_000);

interface IRunsChartProps {
  filters: IRunFilters;
  onFilters: (next: IRunFilters) => void;
  timeZone?: string;
}

export const RunsChart = ({ filters, onFilters, timeZone }: IRunsChartProps) => {
  const queryClient = useQueryClient();
  const [now, setNow] = useState(() => Date.now());
  const pointed = useRef(false);
  const stale = useRef(false);

  const filterKey = JSON.stringify(filters);
  useEffect(() => setNow(Date.now()), [filterKey]);

  const tickMs = tickOf(filters.minutes);
  const windowEnd = Math.ceil(now / tickMs) * tickMs;
  const query = useMemo(
    () => ({ ...runQuery(filters, windowEnd), buckets: CHART_BUCKETS }),
    [filters, windowEnd],
  );
  const pinned = Boolean(filters.from && filters.to && !filters.q);

  const chart = useQuery({
    queryKey: runKeys.chart(query),
    queryFn: () => runChart(query),
    placeholderData: keepPreviousData,
  });

  // A rolling window follows the clock to take the new run in; a pinned one only recounts.
  const refresh = () => {
    const at = Date.now();
    if (!pinned && Math.ceil(at / tickMs) * tickMs !== windowEnd) setNow(at);
    else void queryClient.invalidateQueries({ queryKey: [...runKeys.all, 'chart'] });
  };

  // Bars must not move under a pointer that is reading or dragging them; it catches up on leave.
  useRunChartLive(filters.flowId, () => {
    if (pointed.current) stale.current = true;
    else refresh();
  });

  const buckets = useMemo(() => runChartBuckets(chart.data), [chart.data]);

  const pick = (range: IBucketRange) => {
    const first = buckets[range.from];
    const last = buckets[range.to];
    if (!first || !last) return;
    onFilters({ ...filters, from: first.start, to: last.end, q: undefined });
  };

  const body = () => {
    if (chart.isError) {
      return <ErrorState error={chart.error} onRetry={() => void chart.refetch()} />;
    }
    if (chart.isPending) return <LoadingState rows={2} />;
    const total = chart.data.total;
    return (
      <>
        <StatusBars
          buckets={buckets}
          onSelect={pick}
          timeZone={timeZone}
          now={now}
          series={RUN_CHART_SERIES}
          unit="runs"
          label={`${total.toLocaleString('en-US')} runs over ${buckets.length} buckets`}
        />
        <div className="mt-2 flex flex-wrap items-center gap-4 font-mono text-micro text-faint">
          {RUN_GROUPS.filter(
            (group) => group.key !== 'inProgress' || buckets.some((b) => b.byStatus.inProgress > 0),
          ).map((group) => {
            const dot = (
              <span aria-hidden className={cn('size-dot rounded-full', DOT[group.band])} />
            );
            const { show } = group;
            if (!show) {
              return (
                <span key={group.key} className="flex items-center gap-label">
                  {dot}
                  {group.legend}
                </span>
              );
            }
            const on = filters.show === show;
            return (
              <button
                key={group.key}
                type="button"
                aria-pressed={on}
                title={on ? 'Back to every run' : 'Only these, in the chart and the list'}
                onClick={() => onFilters({ ...filters, show: on ? 'all' : show })}
                className={cn(
                  'flex items-center gap-label rounded-control hover:text-primary',
                  on && 'text-primary',
                )}
              >
                {dot}
                {group.legend}
              </button>
            );
          })}
          {total === 0 && <span>no runs in this window</span>}
          <span className="ml-auto">
            {rangeTime(chart.data.from, chart.data.to, now, timeZone)}
          </span>
        </div>
      </>
    );
  };

  return (
    <div
      className="border-b border-border-sunken px-page pt-4 pb-3"
      onPointerEnter={() => {
        pointed.current = true;
      }}
      onPointerLeave={() => {
        pointed.current = false;
        if (!stale.current) return;
        stale.current = false;
        refresh();
      }}
    >
      {body()}
    </div>
  );
};
